import React, { useState, useEffect, useCallback } from 'react';
import { Table, Avatar, Tag, Spin, Alert, Button } from 'antd';
import { HistoryOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import './History.css';

// Component สำหรับหน้า History
function History({ userData }) {
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ฟังก์ชันสำหรับดึงข้อมูล History จาก Firestore
  const fetchHistoryData = useCallback(async () => {
    if (!userData?.username) {
      setError('ไม่พบข้อมูลผู้ใช้');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // ดึงข้อมูลจาก Collection History ของผู้ใช้
      const historyCollectionRef = collection(db, "Competition-Training", userData.username, "History");
      const querySnapshot = await getDocs(historyCollectionRef);
      
      const fetchedHistory = [];
      querySnapshot.forEach((document) => {
        const data = document.data();
        
        fetchedHistory.push({
          key: document.id,
          ...data,
          resetDateFormatted: data.resetDate ? new Date(data.resetDate).toLocaleDateString('th-TH', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'ไม่ระบุวันที่'
        });
      });

      // เรียงลำดับตามวันที่รีเซ็ต (ใหม่สุดก่อน)
      fetchedHistory.sort((a, b) => {
        if (!a.resetDate || !b.resetDate) return 0;
        return new Date(b.resetDate) - new Date(a.resetDate);
      });

      setHistoryData(fetchedHistory);
    } catch (error) {
      console.error("Error fetching history data:", error);
      setError(`เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [userData?.username]);

  // Effect สำหรับโหลดข้อมูลเมื่อ component mount
  useEffect(() => {
    fetchHistoryData();
  }, [fetchHistoryData]);

  // ฟังก์ชันสำหรับกลับไปยังหน้า Leaderboard
  const handleBackToLeaderboard = () => {
    navigate('/leaderboard');
  };

  // ฟังก์ชันสำหรับกลับไปยังหน้า Exercies
  const handleBackToExercies = () => {
    navigate('/exercies');
  };

  // คอลัมน์สำหรับ Ant Design Table
  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      align: 'center',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'วันที่รีเซ็ต',
      dataIndex: 'resetDateFormatted',
      key: 'resetDateFormatted',
      width: 150,
      render: (text) => (
        <div style={{ fontSize: '12px' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'ระดับรวม',
      dataIndex: 'totalLevel',
      key: 'totalLevel',
      width: 80,
      align: 'center',
      render: (totalLevel) => (
        <Tag color="blue" style={{ fontSize: '12px', padding: '2px 6px' }}>
          {totalLevel || 0}
        </Tag>
      ),
    },
    {
      title: 'คะแนน',
      dataIndex: 'score',
      key: 'score',
      width: 80,
      align: 'center',
      render: (score) => (
        <Tag color="gold" style={{ fontSize: '12px', padding: '2px 6px' }}>
          {score || 0}
        </Tag>
      ),
    },
    {
      title: 'Silver',
      dataIndex: 'showSilver',
      key: 'showSilver',
      width: 70,
      align: 'center',
      render: (showSilver) => (
        <Tag color="default" style={{ fontSize: '10px', padding: '1px 4px' }}>
          🥈 {showSilver || 0}
        </Tag>
      ),
    },
    {
      title: 'Gold',
      dataIndex: 'showGold',
      key: 'showGold',
      width: 70,
      align: 'center',
      render: (showGold) => (
        <Tag color="gold" style={{ fontSize: '10px', padding: '1px 4px' }}>
          🥇 {showGold || 0}
        </Tag>
      ),
    },
    {
      title: 'Diamond',
      dataIndex: 'showDiamond',
      key: 'showDiamond',
      width: 70,
      align: 'center',
      render: (showDiamond) => (
        <Tag color="purple" style={{ fontSize: '10px', padding: '1px 4px' }}>
          💎 {showDiamond || 0}
        </Tag>
      ),
    },
  ];

  return (
    <div className="history-container">
      <div className="history-header">
        <div className="logo-container">
          <img
            src="/Wisdom-Logo.png"
            alt="Wisdom Logo"
            className="logo-image"
          />
        </div>

        {/* ข้อความอยู่ตรงกลาง */}
        <h1 className="history-title">
          HISTORY
        </h1>
        
        {/* ปุ่ม "กลับ" อยู่ด้านซ้าย */}
        <div className="button-group">
          <button onClick={handleBackToLeaderboard} className="back-button">
            Leaderboard
          </button>
          <button onClick={handleBackToExercies} className="back-button">
            กลับ
          </button>
        </div>
      </div>

      {error && (
        <Alert
          message="ข้อผิดพลาด"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>กำลังโหลด HISTORY...</p>
        </div>
      ) : (
        <div className="history-content">
          {historyData.length === 0 ? (
            <div className="no-data">
              <HistoryOutlined style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }} />
              <h3>ไม่มีข้อมูล HISTORY</h3>
              <p>คุณยังไม่เคยทำการรีเซ็ตข้อมูล</p>
            </div>
          ) : (
            <div className="table-container">
              <Table
                columns={columns}
                dataSource={historyData}
                pagination={{
                  pageSize: window.innerWidth <= 768 ? 5 : 10,
                  showSizeChanger: window.innerWidth > 768,
                  showQuickJumper: window.innerWidth > 768,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} จาก ${total} รายการ`,
                  simple: window.innerWidth <= 480,
                }}
                scroll={{ x: window.innerWidth <= 480 ? 500 : 600 }}
                size={window.innerWidth <= 480 ? "small" : "middle"}
                className="history-table"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default History;
